import Image from "next/image";
import { FadeInIntersection } from "@/common/components/fade-in.component";
import { Button } from "@/common/components-2/button";
import { Icon } from "@/common/components-2/icon";
import { MigrationLink } from "@/common/components-2/migration-link";
import { SanitizedContent } from "@/common/components-2/sanitized-content";
import { isNil } from "@/common/helpers/lang";
import type { Image as ImageType } from "@/common/typings/media";
import { useTranslations } from "@/services/i18n";
import { cn } from "@/services/styles/cn";
import type { HowItWorksSectionProps } from "./how-it-works-section.types";

type StepKey = "step2" | "step3" | "step4";
const stepKey = (index: number) => `step${index + 1}` as StepKey;

export function ImageComponent({ index, image }: { index: number; image: ImageType }) {
  return (
    <div className={`lg:w-full lg:max-w-[60%] lg:flex-1 ${index % 2 === 0 ? "lg:order-1" : "lg:order-2"}`}>
      <Image src={image.url} alt={image.alt} width={632} height={632} className="mx-auto" />
    </div>
  );
}

export function DescriptionComponent({ description, className }: { description: string; className?: string }) {
  return (
    <SanitizedContent
      as="div"
      className={cn(
        "mt-10 [&>ul>li]:pl-3 [&>ul]:ml-6 [&>ul]:flex [&>ul]:list-image-[url(/next-assets/list-check.svg)] [&>ul]:flex-col [&>ul]:gap-2",
        className,
      )}
      content={description}
    />
  );
}

export function HeadingComponent({ heading, className }: { heading: string; className?: string }) {
  return (
    <SanitizedContent
      as="h3"
      content={heading}
      className={cn("text-center font-serif text-3xl lg:text-left", className)}
    />
  );
}

export function StepTitleComponent({ index }: { index: number }) {
  const t = useTranslations("components.howItWorksSection");
  // This hack is to make the index start from 2 instead of 1, the reason is that the first step is another CMS model
  const getIndex = (index: number) => ({
    label: index + 2,
    header: t(stepKey(index + 1)),
  });

  return (
    <h4 className="mb-4 flex justify-center gap-2 font-medium text-dodger-blue-700 uppercase lg:justify-start">
      <span className="flex size-6 items-center justify-center rounded-full border border-dodger-blue-700">
        {getIndex(index).label}
      </span>
      {getIndex(index).header}
    </h4>
  );
}

export function TitleComponent({ title, icon, className }: { title: string; icon: string; className?: string }) {
  return (
    <h4 className={cn("mb-4 flex items-center gap-2 font-medium text-dodger-blue-700 uppercase", className)}>
      <Icon name={icon} className="text-2xl" />
      {title}
    </h4>
  );
}

export function WrapperComponent({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={cn("my-12 flex flex-col lg:my-0 lg:flex-row lg:items-center lg:gap-16 lg:py-24", className)}
      style={style}
    >
      {children}
    </div>
  );
}

export function ContentComponent({
  children,
  index,
  className,
}: {
  children: React.ReactNode;
  index: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        `pt-12 lg:w-full lg:max-w-[40%] lg:flex-1 lg:p-0 lg:py-12 ${index % 2 === 0 ? "order-1 lg:order-2" : "order-2 lg:order-1"}`,
        className,
      )}
    >
      {children}
    </div>
  );
}

export function HowItWorksSection({ steps }: HowItWorksSectionProps) {
  const isButton = (buttonLabel?: string | null, buttonUrl?: string | null) => !isNil(buttonLabel) && !isNil(buttonUrl);

  return (
    <div className="px-4">
      {steps.map(({ title, description, buttonLabel, buttonUrl, image }, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: index is the only unique identifier
        <FadeInIntersection rootMargin="0px 0px -300px 0px" delay={100} asChild animation="fadeUp" key={index}>
          <WrapperComponent>
            <ContentComponent index={index}>
              <StepTitleComponent index={index} />
              <HeadingComponent heading={title} />
              <DescriptionComponent description={description} />
              {isButton(buttonLabel, buttonUrl) ? (
                <Button variant="primary" className="mt-14 w-full lg:w-max" size="lg" asChild>
                  <MigrationLink href={buttonUrl ?? "/"}>{buttonLabel}</MigrationLink>
                </Button>
              ) : null}
            </ContentComponent>
            <ImageComponent index={index} image={image} />
          </WrapperComponent>
        </FadeInIntersection>
      ))}
    </div>
  );
}
